// Sales · Trial panel — the per-COMPANY commercial trial of a tenant's platform
// allocation (migration 28: license_org_allocations.{is_trial,trial_expires_at,
// trial_vendor_limit}). Shows the live trial state (days left / expired + vendor
// cap), an admin-only adjust/start/convert control, and the append-only trial
// history ('historical trials on a company') newest-first.
//
// SCOPE — this is the COMMERCIAL trial clock on the allocation itself, NOT the
// trust MODULE trial (that lives on the TPRM7 org plane). Every change is written
// to trial_events by the backend, which this panel then re-reads.
//
// Mounted from SalesAccount for org_kind==='tenant', beside the allocation card, and
// (OF-168 / D-53) from LicenseDetail for a SAAS licence: a free Trust Center org's
// "try the full platform" trial IS this same per-company clock on its allocation.
// The title / sub / start & idle labels are overridable so the saas mount reads in
// its own words; the defaults are the Sales mount's, unchanged.
// Mutations are gated on canMutate (admin + non-audit workspace) exactly like the
// page's other write controls; the backend independently enforces requireAdmin.
import React, { useEffect, useState } from 'react';
import { Card, CardHead, Btn, Table, Td, Row, Mono } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from '@/components/shared/ToastProvider';
import { formatRelativeTime } from '@/lib/utils';
import { adjustTrial, getTrialHistory, type TrialEvent, type TrialAction } from '@/lib/api/accounts';

// Only what the panel reads. TenantAllocation (Sales › Account) satisfies it, and
// so does the saas lookup's allocation (trust.ts SaasTrialAllocation), which has
// no billing/usage fields to fake. (`seats` is nullable on the saas lookup.)
export interface TrialAllocation {
  allocation_id: string;
  is_trial: boolean;
  trial_expires_at: string | null;
  trial_vendor_limit: number | null;
  seats: number | null;
}

// Operator defaults for a fresh trial: a 30-day clock and a 5-vendor ceiling.
const DEFAULT_TRIAL_DAYS = 30;
const DEFAULT_VENDOR_CAP = 5;

// yyyy-mm-dd for a <input type="date"> value.
function toDateInputValue(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function defaultExpiryInput(): string {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_TRIAL_DAYS);
  return toDateInputValue(d);
}
// A date-input day → an ISO instant at the END of that local day, so "N days left"
// counts the picked day itself. The backend accepts a Z/offset datetime.
function dateInputToISO(value: string): string {
  return new Date(`${value}T23:59:59`).toISOString();
}

// Whole days between now and an expiry instant (ceil, so any part of today counts).
function daysLeft(expiresAt: string): number {
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
}

const ACTION_LABEL: Record<TrialAction, string> = {
  started: 'Trial started',
  extended: 'Trial extended',
  shortened: 'Trial shortened',
  expired: 'Trial expired',
  converted: 'Converted to paid',
  vendor_cap_changed: 'Vendor cap changed',
};
const ACTION_STYLE: Record<TrialAction, { bg: string; fg: string; icon: string }> = {
  started: { bg: '#DCFCE7', fg: '#166534', icon: 'play-circle' },
  extended: { bg: '#DBEAFE', fg: '#1E40AF', icon: 'calendar-plus' },
  shortened: { bg: '#FEF3C7', fg: '#92400E', icon: 'calendar-minus' },
  expired: { bg: '#FEE2E2', fg: '#991B1B', icon: 'alert-triangle' },
  converted: { bg: '#E0E7FF', fg: '#3730A3', icon: 'check-circle' },
  vendor_cap_changed: { bg: '#F3E8FF', fg: '#6B21A8', icon: 'sliders-horizontal' },
};

function fmtDate(v: string | null): string {
  return v ? new Date(v).toLocaleDateString() : '—';
}
function fmtCap(v: number | null): string {
  return v == null ? 'default' : String(v);
}

export function TrialPanel({
  allocation,
  canMutate,
  accent,
  onChanged,
  title = 'Commercial trial',
  sub = 'Per-company trial clock & vendor cap on this allocation · every change is logged',
  startLabel = 'Start trial',
  idleLabel = 'Paid / no trial',
  allowConvert = true,
}: {
  allocation: TrialAllocation;
  canMutate: boolean;
  accent: string;
  onChanged: () => void | Promise<void>;
  title?: string;
  sub?: string;
  /** Label of the button (and form) that opens a trial on an org that is not on one. */
  startLabel?: string;
  /** Status pill for an org that is not on trial. */
  idleLabel?: string;
  /**
   * Offer "Convert (end trial)". Right for a commercial tenant (it flips them to
   * paid billing); WRONG for a free saas org, where "converted" means a purchase
   * through checkout (attributed natively) and clearing the flag would leave a
   * perpetual free non-trial allocation — against D-53/D-17.
   */
  allowConvert?: boolean;
}) {
  const allocationId = allocation.allocation_id;
  const isTrial = allocation.is_trial;
  const expiresAt = allocation.trial_expires_at;
  const left = expiresAt ? daysLeft(expiresAt) : null;
  const expired = left != null && left <= 0;

  // Adjust/start form (inline, admin-only).
  const [formOpen, setFormOpen] = useState(false);
  const [expiryInput, setExpiryInput] = useState(defaultExpiryInput());
  const [capInput, setCapInput] = useState<string>(String(DEFAULT_VENDOR_CAP));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmConvert, setConfirmConvert] = useState(false);

  // History timeline.
  const [history, setHistory] = useState<TrialEvent[]>([]);
  const [histLoading, setHistLoading] = useState(true);
  const [histError, setHistError] = useState<string | null>(null);

  const loadHistory = async () => {
    setHistLoading(true);
    setHistError(null);
    try {
      setHistory(await getTrialHistory(allocationId));
    } catch (e) {
      setHistError(e instanceof Error ? e.message : 'Failed to load trial history');
    } finally {
      setHistLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the allocation changes
  useEffect(() => { loadHistory(); }, [allocationId]);

  const openForm = () => {
    // Prefill from the current trial when adjusting; defaults when starting fresh.
    setExpiryInput(expiresAt ? toDateInputValue(new Date(expiresAt)) : defaultExpiryInput());
    setCapInput(allocation.trial_vendor_limit != null ? String(allocation.trial_vendor_limit) : String(DEFAULT_VENDOR_CAP));
    setReason('');
    setError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    if (!expiryInput) { setError('Pick a trial expiry date.'); return; }
    const capTrim = capInput.trim();
    const cap = capTrim === '' ? null : Number(capTrim);
    if (cap != null && (!Number.isInteger(cap) || cap < 0)) { setError('Vendor cap must be a whole number ≥ 0 (or blank to clear).'); return; }
    setBusy(true);
    setError(null);
    try {
      await adjustTrial(allocationId, {
        trial_expires_at: dateInputToISO(expiryInput),
        trial_vendor_limit: cap,
        reason: reason.trim() || undefined,
      });
      toast({ kind: 'success', title: isTrial ? 'Trial updated' : 'Trial started', body: `Deadline ${new Date(dateInputToISO(expiryInput)).toLocaleDateString()} · cap ${cap == null ? 'default' : cap}.` });
      setFormOpen(false);
      await Promise.all([onChanged(), loadHistory()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the trial change.');
    } finally {
      setBusy(false);
    }
  };

  const doConvert = async () => {
    setBusy(true);
    setError(null);
    try {
      await adjustTrial(allocationId, { trial_expires_at: null, reason: 'Converted to paid from account console' });
      toast({ kind: 'success', title: 'Converted to paid', body: 'Trial ended; the org now bills as paid.' });
      setConfirmConvert(false);
      await Promise.all([onChanged(), loadHistory()]);
    } catch (e) {
      setConfirmConvert(false);
      toast({ kind: 'error', title: 'Convert failed', body: e instanceof Error ? e.message : 'Could not convert.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ overflow: 'hidden', marginTop: 16 }}>
      <CardHead
        title={title}
        sub={sub}
        right={canMutate ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {isTrial ? (
              <>
                <Btn sm icon="sliders-horizontal" disabled={busy} onClick={openForm}>Adjust trial</Btn>
                {allowConvert && <Btn sm icon="check-circle" disabled={busy} onClick={() => setConfirmConvert(true)}>Convert (end trial)</Btn>}
              </>
            ) : (
              <Btn sm kind="primary" accent={accent} icon="gift" disabled={busy} onClick={openForm}>{startLabel}</Btn>
            )}
          </div>
        ) : undefined}
      />

      <div style={{ padding: '16px 20px', display: 'grid', gap: 16 }}>
        {/* Current state */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'center' }}>
          <div>
            <div style={label}>Status</div>
            {isTrial
              ? <Pill bg={expired ? '#FEE2E2' : '#DCFCE7'} fg={expired ? '#991B1B' : '#166534'} dot>{expired ? 'Trial expired' : 'On trial'}</Pill>
              : <Pill bg="#E0E7FF" fg="#3730A3">{idleLabel}</Pill>}
          </div>
          <div>
            <div style={label}>Time left</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: expired ? '#DC2626' : 'var(--text-primary)' }}>
              {!isTrial || left == null ? '—' : expired ? 'Expired' : `${left} day${left !== 1 ? 's' : ''} left`}
            </span>
          </div>
          <div>
            <div style={label}>Trial expiry</div>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{isTrial ? fmtDate(expiresAt) : '—'}</span>
          </div>
          <div>
            <div style={label}>Trial vendor cap</div>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {isTrial ? (allocation.trial_vendor_limit != null ? allocation.trial_vendor_limit : `default (${allocation.seats})`) : '—'}
            </span>
          </div>
        </div>

        {error && !formOpen && <AlertBanner kind="error">{error}</AlertBanner>}

        {/* Adjust / start form (inline, admin-only) */}
        {canMutate && formOpen && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: 'var(--bg-secondary)', display: 'grid', gap: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{isTrial ? 'Adjust trial' : startLabel}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <label style={fieldWrap}>
                <span style={label}>Trial expires</span>
                <input type="date" value={expiryInput} onChange={(e) => setExpiryInput(e.target.value)} style={input} />
              </label>
              <label style={fieldWrap}>
                <span style={label}>Vendor cap <span style={{ textTransform: 'none', fontWeight: 400 }}>(blank = default)</span></span>
                <input type="number" min={0} step={1} value={capInput} onChange={(e) => setCapInput(e.target.value)} placeholder="default" style={{ ...input, width: 120 }} />
              </label>
              <label style={{ ...fieldWrap, flex: 1, minWidth: 200 }}>
                <span style={label}>Reason <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></span>
                <input type="text" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="e.g. extended per sales agreement" style={{ ...input, width: '100%' }} />
              </label>
            </div>
            {error && <AlertBanner kind="error">{error}</AlertBanner>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn sm disabled={busy} onClick={() => { setFormOpen(false); setError(null); }}>Cancel</Btn>
              <Btn sm kind="primary" accent={accent} icon={busy ? 'loader' : (isTrial ? 'save' : 'gift')} disabled={busy} onClick={submit}>
                {busy ? 'Saving…' : isTrial ? 'Save trial' : startLabel}
              </Btn>
            </div>
          </div>
        )}

        {/* History timeline — historical trials on this company, newest-first */}
        <div>
          <div style={{ ...label, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="history" size={13} color="var(--text-muted)" /> Trial history
          </div>
          {histLoading ? (
            <div style={muted}>Loading trial history…</div>
          ) : histError ? (
            <AlertBanner kind="error">{histError}</AlertBanner>
          ) : history.length === 0 ? (
            <div style={muted}>No trial changes recorded for this company yet.</div>
          ) : (
            <Table cols={[{ label: 'Change' }, { label: 'Expiry (old → new)' }, { label: 'Cap (old → new)' }, { label: 'Actor' }, { label: 'Reason' }, { label: 'When' }]}>
              {history.map((ev) => {
                const s = ACTION_STYLE[ev.action] || { bg: 'var(--bg-muted)', fg: 'var(--text-secondary)', icon: 'circle' };
                return (
                  <Row key={ev.id}>
                    <Td><Pill bg={s.bg} fg={s.fg}><Icon name={s.icon} size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />{ACTION_LABEL[ev.action] || ev.action}</Pill></Td>
                    <Td><Mono style={{ fontSize: 12 }}>{fmtDate(ev.old_expires_at)} → {fmtDate(ev.new_expires_at)}</Mono></Td>
                    <Td><Mono style={{ fontSize: 12 }}>{fmtCap(ev.old_vendor_cap)} → {fmtCap(ev.new_vendor_cap)}</Mono></Td>
                    <Td style={{ color: 'var(--text-secondary)' }}>{ev.actor || '—'}</Td>
                    <Td style={{ color: 'var(--text-secondary)', maxWidth: 220, whiteSpace: 'normal' }}>{ev.reason || '—'}</Td>
                    <Td style={{ color: 'var(--text-secondary)' }}><span title={new Date(ev.created_at).toLocaleString()}>{formatRelativeTime(ev.created_at)}</span></Td>
                  </Row>
                );
              })}
            </Table>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmConvert}
        title="Convert this trial to paid?"
        body="This ends the commercial trial now (clears the trial clock and any per-trial vendor cap). The org bills as paid on the next phone-home. This is logged to the trial history."
        confirmLabel="Convert to paid"
        busy={busy}
        onConfirm={doConvert}
        onCancel={() => setConfirmConvert(false)}
      />
    </Card>
  );
}

const label: React.CSSProperties = { fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 };
const muted: React.CSSProperties = { padding: '18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 };
const fieldWrap: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const input: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)' };

export default TrialPanel;
